var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { memo, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { useRedesignModal } from 'root/src/client/v2/common/contexts/redesignModal.context';
import { useUserDropEventLandingShortUrlQuery } from 'root/src/client/v2/drop/hooks/useDropEventLandingShortUrlQuery';
import { RedesignInitialProductDisplayModal } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignInitialProductDisplayModal';
import { reclaimKeySelectionFormSchema } from 'root/src/client/v2/drop/features/KeySelectionModal/components/ReclaimKeySelectionModal';
import { RedesignInitialProductSelect } from 'root/src/client/v2/drop/features/KeySelectionModal/components/RedesignKeySelectionModal';
import { KeySelectionModalContext } from 'root/src/client/v2/drop/features/KeySelectionModal/hooks/useKeySelectionModal';
import { RedesignModalShell } from 'root/src/client/v2/common/components/RedesignModalShell';
import { Button } from 'root/src/client/v2/common/components/ui/button';
import { useAsyncAction } from 'root/src/client/v2/common/hooks/useAsyncAction';
const getDefaultValues = (drop) => {
    var _a, _b;
    const initialProduct = (_a = drop.initialProduct) !== null && _a !== void 0 ? _a : [];
    return initialProduct.length === 1 ? { productId: (_b = initialProduct.at(0)) === null || _b === void 0 ? void 0 : _b.id } : undefined;
};
export const RedesignReclaimKeySelectionModal = memo(({ drop, onSuccess }) => {
    const redesignModal = useRedesignModal();
    const { loading, success, error, run } = useAsyncAction();
    const reclaimMutation = trpc.rewards.reclaimCreatorInitialReward.useMutation();
    const shortUrlQuery = useUserDropEventLandingShortUrlQuery(drop.id, { enabled: false });
    const form = useForm({ resolver: zodResolver(reclaimKeySelectionFormSchema), defaultValues: getDefaultValues(drop) });
    const contextValue = useMemo(() => ({ dropEvent: drop }), [drop]);
    const onSubmit = form.handleSubmit((values) => {
        void run(() => __awaiter(void 0, void 0, void 0, function* () {
            const products = yield reclaimMutation.mutateAsync({ dropId: drop.id, productId: values.productId });
            const res = yield shortUrlQuery.refetch();
            if (res.isError) {
                throw new Error(res.error.message);
            }
            // Success transitions to the product display modal (no extra success tick needed).
            redesignModal.show(<RedesignInitialProductDisplayModal dropEvent={drop} shortUrl={res.data} products={products}/>, { closeable: false });
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
        }), {
            successHold: 0,
            toast: {
                loading: 'Reclaiming reward...',
                success: 'Reward reclaimed',
                error: caught => (caught instanceof Error ? caught.message : 'Something went wrong'),
            },
        });
    });
    return (<RedesignModalShell className="sm:min-w-[460px] sm:max-w-xl">
			<div className="flex flex-col items-center gap-1 text-center">
				<h2 className="font-display text-xl font-bold text-foreground">Re-claim your reward</h2>
				<p className="text-sm text-muted-foreground">Pick the option below for the reward you want to claim.</p>
			</div>

			<KeySelectionModalContext.Provider value={contextValue}>
				<FormProvider {...form}>
					<form onSubmit={onSubmit} className="flex w-full flex-col items-center gap-6">
						<RedesignInitialProductSelect />
						<p className="text-center text-xs font-medium text-neon-green">Delivering without a key increases your delivery rate.</p>
						<Button type="submit" variant="brand" loading={loading} success={success} error={error} className="w-full max-w-xs rounded-lg text-sm font-semibold">
							Reclaim reward
						</Button>
					</form>
				</FormProvider>
			</KeySelectionModalContext.Provider>
		</RedesignModalShell>);
});
